import type { Exercise, RoutineExercise, SetRecord, WorkoutSession } from './types'

export type ProgressionSuggestion =
  | { kind: 'load'; label: string; weight: number; reps: number }
  | { kind: 'reps'; label: string; reps: number }
  | { kind: 'duration'; label: string; duration: number }
  | { kind: 'assistance'; label: string; assistance: number }

export function lastExerciseSets(
  sessions: WorkoutSession[],
  exerciseId: string,
  memoryWeeks = 0,
): SetRecord[] {
  const cutoff = memoryWeeks > 0 ? Date.now() - memoryWeeks * 7 * 86_400_000 : 0
  const session = [...sessions]
    .filter((item) => item.status !== 'in_progress')
    .filter((item) => !cutoff || new Date(item.startedAt).getTime() >= cutoff)
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
    .find((item) => item.sets.some((set) => set.exerciseId === exerciseId))
  return (session?.sets ?? [])
    .filter((set) => set.exerciseId === exerciseId)
    .sort((a, b) => a.setNumber - b.setNumber)
}

export function progressionSuggestion(
  exercise: Exercise,
  target: RoutineExercise,
  previous: SetRecord[],
): ProgressionSuggestion | undefined {
  const sets = previous.slice(0, target.sets)
  if (sets.length !== target.sets) return undefined

  if (exercise.metrics.includes('duration') && !exercise.metrics.includes('reps')) {
    const durations = sets.map((set) => set.durationSeconds ?? 0)
    if (durations.every((duration) => duration >= target.maxReps * 60)) {
      return { kind: 'duration', duration: 60, label: '+1 min' }
    }
  }

  const reps = sets.map((set) => set.reps ?? 0)
  const reachedTop = reps.every((value) => value >= target.maxReps)

  if (reachedTop && exercise.metrics.includes('assistance')) {
    const assistance = Math.max(...sets.map((set) => set.assistance ?? 0))
    if (assistance > 0) {
      const next = Math.max(0, assistance - exercise.increment)
      return { kind: 'assistance', assistance: next, label: `Ayuda ${formatNumber(next)} kg` }
    }
  }

  if (reachedTop && exercise.metrics.includes('weight')) {
    const current = Math.max(...sets.map((set) => set.weight ?? 0))
    const next = current + exercise.increment
    return { kind: 'load', weight: next, reps: target.minReps, label: `Sube a ${formatNumber(next)}` }
  }

  const firstBelow = reps.find((value) => value < target.maxReps)
  if (firstBelow !== undefined) {
    const next = Math.min(target.maxReps, firstBelow + 1)
    return { kind: 'reps', reps: next, label: `Busca ${next} reps` }
  }
}

export function estimateChange(current: SetRecord[], previous: SetRecord[]): number | undefined {
  const score = (sets: SetRecord[]) =>
    sets.reduce((total, set) => {
      if (set.durationSeconds) return total + set.durationSeconds
      return total + (set.reps ?? 0) * Math.max(set.weight ?? 0, 1)
    }, 0)
  const before = score(previous)
  if (!before) return undefined
  return ((score(current) - before) / before) * 100
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 }).format(value)
}
